// Shared constants & pure helpers used by the Dashboard and its custom hooks.
// Kept side-effect free so tests/hooks can import without pulling React.

// Default fallback start (used before GPS resolves).
export const DEFAULT_START = { name: "Valle Brembana (BG)", lat: 45.9057, lng: 9.6675 };

// Average speeds per vehicle (km/h) — used for time↔distance CONFIGURATOR
// preview (before the backend responds). Backend uses the same model in
// _realistic_duration_min. Paved-road speeds; off-road ratio is applied
// via `(1 - offroad/200)` = up to 50% penalty at 100% off-road.
//   MOTO   50 km/h → 25 km/h @100% off-road (user directive Phase 5.2)
//   E-BIKE 22 km/h → 11 km/h @100% off-road
//   WALK    5 km/h →  2.5 km/h @100% off-road
export const SPEEDS = { moto: 50, ebike: 22, walk: 5 };

// Curated zones for the "Giri pronti / Community" archive filter.
export const ZONES = [
  { id: "alpi-lombarde", name: "Alpi Lombarde", lat: 45.9, lng: 9.9, radius: 100 },
  { id: "dolomiti", name: "Dolomiti · Trentino", lat: 46.5, lng: 11.7, radius: 100 },
  { id: "valle-aosta", name: "Valle d'Aosta", lat: 45.74, lng: 7.32, radius: 80 },
  { id: "lago-garda", name: "Lago di Garda", lat: 45.64, lng: 10.68, radius: 60 },
  { id: "appennino-emiliano", name: "Appennino Tosco-Emiliano", lat: 44.2, lng: 10.7, radius: 90 },
  { id: "chianti", name: "Chianti · Toscana", lat: 43.5, lng: 11.3, radius: 70 },
  { id: "sibillini", name: "Sibillini · Marche", lat: 42.91, lng: 13.26, radius: 70 },
  { id: "gran-sasso", name: "Gran Sasso · Abruzzo", lat: 42.47, lng: 13.56, radius: 80 },
  { id: "amalfi", name: "Costiera Amalfitana", lat: 40.63, lng: 14.6, radius: 50 },
  { id: "sila", name: "Sila · Calabria", lat: 39.33, lng: 16.55, radius: 80 },
  { id: "etna", name: "Etna · Sicilia", lat: 37.75, lng: 14.99, radius: 60 },
  { id: "sardegna", name: "Sardegna", lat: 40.2, lng: 9.2, radius: 150 },
];

// Phase 5.6 (user directive): Nominatim actually parses full addresses
// with street-type words BETTER than the stripped version. The primary
// path is now the user's verbatim text (see useGeocodeSearch). This
// helper is kept as a FALLBACK-only stripper the hook falls back to
// when the verbatim query returns zero results (ambiguous inputs like
// "Frazione Cancelli Fabriano" still resolve on the second try).
//
// The user's text is otherwise sent unchanged.
export function cleanGeoQuery(raw) {
  if (!raw) return "";
  return (
    raw
      // Street-type / place-type prefixes. The trailing `\.?` also catches
      // the abbreviated-and-glued form Italians actually type, e.g.
      // "Fraz.Cancelli" — the dot ends the word, so \b still applies.
      .replace(
        /\b(frazione|fraz\.?|località|localita|loc\.?|via|v\.?|piazza|p\.?zza|corso|viale|v\.le|comune|com\.?|contrada|c\.da|strada|str\.?|paese|borgo|borgata|forania|parrocchia|quartiere)\b/gi,
        " "
      )
      // Civic numbers. Free OSM data has almost no Italian house numbers, so
      // "Cancelli, 128, Fabriano" returns nothing while "Cancelli, Fabriano"
      // resolves. Capped at 4 digits so 5-digit CAPs survive.
      .replace(/(^|[\s,])\d{1,4}[a-z]?(?=$|[\s,])/gi, "$1")
      .replace(/\s*,(\s*,)+/g, ",")
      .replace(/\s+,/g, ",")
      .replace(/\s+/g, " ")
      .replace(/^[\s,]+|[\s,]+$/g, "")
      .trim()
  );
}

// Haversine km — used to filter community rides by proximity to zone center.
export function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
